<script setup>
/**
 * Braintree card fields (the iframes Braintree serves), laid out like our
 * inputs. The parent calls tokenize() for the one-time nonce.
 */
import {onBeforeUnmount, onMounted, ref} from "vue"
import {cardFieldProblem, mountCardFields} from "@/lib/braintree-card"

const props = defineProps({
  token: {type: String, required: true},
  postalCode: Boolean,      // ask for the ZIP (the gateway's card check can need it)
  cardholderName: Boolean,
  zip: String,              // ZIP prefill
})
const emit = defineEmits(['ready', 'error'])

const number = ref(), expiry = ref(), cvv = ref(), zipBox = ref(), nameBox = ref()
const ready = ref(false)
let fields = null

onMounted(async () => {
  try {
    fields = await mountCardFields(props.token, {
      number: number.value, expiry: expiry.value, cvv: cvv.value,
      zip: props.postalCode ? zipBox.value : null, name: props.cardholderName ? nameBox.value : null,
    }, {zip: props.zip})
    ready.value = true
    emit('ready')
  } catch (e) {
    console.log('Braintree card fields failed', e)
    emit('error', (e && e.message) || 'The card form could not be loaded.')
  }
})

async function teardown() {
  const f = fields
  fields = null
  ready.value = false
  if (f) {
    try {
      await f.teardown()
    } catch (e) {
      // already gone
    }
  }
}

onBeforeUnmount(teardown)

/** the one-time nonce; throws an Error saying what to fix */
async function tokenize() {
  if (!fields)
    throw new Error('The card form is not ready yet.')
  try {
    return (await fields.tokenize()).nonce
  } catch (e) {
    throw new Error(cardFieldProblem(e))
  }
}

defineExpose({tokenize, teardown, ready})
</script>

<template>
  <div class="card-fields mb-3">
    <div v-if="!ready" class="text-muted small mb-2">Loading the card form…</div>
    <div v-show="ready">
      <div v-if="cardholderName" class="mb-2">
        <label class="small mb-1">Name on card</label>
        <div ref="nameBox" class="form-control bt-field"/>
      </div>
      <label class="small mb-1">Card number</label>
      <div ref="number" class="form-control bt-field mb-2"/>
      <div class="d-flex">
        <div class="flex-fill mr-2">
          <label class="small mb-1">Expiry</label>
          <div ref="expiry" class="form-control bt-field"/>
        </div>
        <div class="flex-fill" :class="{'mr-2': postalCode}">
          <label class="small mb-1">CVV</label>
          <div ref="cvv" class="form-control bt-field"/>
        </div>
        <div v-if="postalCode" class="flex-fill">
          <label class="small mb-1">ZIP</label>
          <div ref="zipBox" class="form-control bt-field"/>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bt-field {
  height: calc(1.5em + .75rem + 2px);
  padding-top: 0;
  padding-bottom: 0;
}
.bt-field.braintree-hosted-fields-focused {
  border-color: #80bdff;
  box-shadow: 0 0 0 .2rem rgba(0, 123, 255, .25);
}
.bt-field.braintree-hosted-fields-invalid {
  border-color: #dc3545;
}
</style>
